import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { fetchUserDetailForManagement, updateUser, deleteUser, fetchRoleGroups } from '../../services/api';
import './UserDetailPage.css';

function UserDetailPage() {
    const navigate = useNavigate();
    const { userId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [roleGroups, setRoleGroups] = useState([]);

    // 권한 그룹 목록 로드
    useEffect(() => {
        const loadRoleGroups = async () => {
            try {
                const data = await fetchRoleGroups();
                setRoleGroups(data);
            } catch (err) {
                console.error('권한 그룹 목록 조회 실패:', err);
            }
        };
        loadRoleGroups();
    }, []);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    // API에서 사용자 데이터 가져오기
    useEffect(() => {
        const loadUserData = async () => {
            try {
                setLoading(true);
                const data = await fetchUserDetailForManagement(userId);
                
                setUserData(data);
                setFormData({
                    id: data.id,
                    name: data.name,
                    email: data.email,
                    phone: data.phone || '',
                    password: '',
                    confirmPassword: '',
                    roleGroupId: data.roleGroup?.id || null,
                    department: data.department || '',
                    position: data.position || '',
                    status: data.status,
                    lastLogin: data.lastLogin ? formatDateTime(data.lastLogin) : '-',
                    joinDate: data.createdAt ? formatDate(data.createdAt) : '-'
                });
            } catch (err) {
                console.error('사용자 정보 조회 실패:', err);
                alert('사용자 정보를 불러오는데 실패했습니다.');
                navigate('/user');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            loadUserData();
        }
    }, [userId]);

    const [formData, setFormData] = useState({
        id: userId,
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        roleGroupId: null,
        department: '',
        position: '',
        status: 'ACTIVE',
        lastLogin: '-',
        joinDate: '-'
    });

    // 날짜 포맷 함수
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    // 날짜시간 포맷 함수
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '-';
        const date = new Date(dateTimeString);
        return date.toISOString().replace('T', ' ').substring(0, 16);
    };

    // 상태 매핑 함수
    const mapStatus = (apiStatus) => {
        const statusMap = {
            'ACTIVE': '활성',
            'INACTIVE': '비활성',
            'LOCKED': '잠김'
        };
        return statusMap[apiStatus] || '비활성';
    };

    // 상태 역매핑 함수 (한글 -> 영문)
    const unmapStatus = (displayStatus) => {
        const statusMap = {
            '활성': 'ACTIVE',
            '비활성': 'INACTIVE',
            '잠김': 'LOCKED'
        };
        return statusMap[displayStatus] || 'INACTIVE';
    };

    // 선택된 권한 그룹 정보 (편집 모드에서는 formData에서, 읽기 모드에서는 userData에서)
    const selectedRoleGroup = isEditing
        ? roleGroups.find(rg => rg.id === parseInt(formData.roleGroupId))
        : userData?.roleGroup;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/user/${userId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // URL에서 ?edit=true 제거하고 원래 데이터로 복원
            navigate(`/user/${userId}`, { replace: true });
            if (userData) {
                setFormData({
                    id: userData.id,
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone || '',
                    password: '',
                    confirmPassword: '',
                    roleGroupId: userData.roleGroup?.id || null,
                    department: userData.department || '',
                    position: userData.position || '',
                    status: userData.status,
                    lastLogin: userData.lastLogin ? formatDateTime(userData.lastLogin) : '-',
                    joinDate: userData.createdAt ? formatDate(userData.createdAt) : '-'
                });
            }
        } else {
            navigate('/user');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // 비밀번호 확인
        if (formData.password && formData.password !== formData.confirmPassword) {
            alert('비밀번호가 일치하지 않습니다.');
            return;
        }

        try {
            const updateData = {
                name: formData.name,
                phone: formData.phone,
                department: formData.department,
                position: formData.position,
                roleGroupId: parseInt(formData.roleGroupId),
                status: formData.status,
            };

            // 비밀번호가 입력된 경우에만 포함
            if (formData.password) {
                updateData.password = formData.password;
            }

            
            await updateUser(userId, updateData);

            alert('사용자 정보가 수정되었습니다.');

            // 데이터 새로고침
            const updatedData = await fetchUserDetailForManagement(userId);
            setUserData(updatedData);
            setFormData({
                id: updatedData.id,
                name: updatedData.name,
                email: updatedData.email,
                phone: updatedData.phone || '',
                password: '',
                confirmPassword: '',
                roleGroupId: updatedData.roleGroup?.id || null,
                department: updatedData.department || '',
                position: updatedData.position || '',
                status: updatedData.status,
                lastLogin: updatedData.lastLogin ? formatDateTime(updatedData.lastLogin) : '-',
                joinDate: updatedData.createdAt ? formatDate(updatedData.createdAt) : '-'
            });

            // 저장 후 읽기 모드로 전환
            navigate(`/user/${userId}`, { replace: true });
        } catch (error) {
            console.error('사용자 정보 수정 실패:', error);
            alert('사용자 정보 수정에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('정말로 이 사용자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) {
            try {
                
                await deleteUser(userId);
                alert('사용자가 삭제되었습니다.');
                navigate('/user');
            } catch (error) {
                console.error('사용자 삭제 실패:', error);
                alert('사용자 삭제에 실패했습니다.');
            }
        }
    };

    const getInitials = (name) => {
        return name.charAt(0);
    };

    if (loading) {
        return (
            <MainLayout>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    데이터를 불러오는 중...
                </div>
            </MainLayout>
        );
    }

    if (!userData) {
        return (
            <MainLayout>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    사용자 정보를 찾을 수 없습니다.
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="user-detail-container">
                {/* Header Section */}
                <div className="user-detail-header">
                    <div className="user-detail-header-left">
                        <div className="back-btn" onClick={() => navigate('/user')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <div className="user-detail-title">사용자 상세 정보</div>
                            <p className="user-detail-subtitle">사용자 계정 정보 조회 및 수정</p>
                        </div>
                    </div>
                    <div className="user-detail-header-right">
                        {!isEditing ? (
                            <>
                                <button className="delete-header-btn" onClick={handleDelete}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    삭제
                                </button>
                                <button className="edit-header-btn" onClick={handleEdit}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    수정
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="cancel-btn" onClick={handleCancel}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    취소
                                </button>
                                <button className="save-btn" onClick={handleSave}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    저장
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="user-detail-content">
                    {/* User Profile Card */}
                    <div className="user-profile-card">
                        <div className="user-profile-avatar-large">
                            {getInitials(formData.name)}
                        </div>
                        <div className="user-profile-info">
                            <h2 className="user-profile-name">{formData.name}</h2>
                            <p className="user-profile-email">{formData.email}</p>
                            <span className={`status-badge-large ${mapStatus(formData.status) === '활성' ? 'status-active' : 'status-inactive'}`}>
                                {mapStatus(formData.status)}
                            </span>
                        </div>
                        <div className="user-profile-meta">
                            <div className="meta-item">
                                <span className="meta-label">가입일</span>
                                <span className="meta-value">{formData.joinDate}</span>
                            </div>
                            <div className="meta-item">
                                <span className="meta-label">마지막 로그인</span>
                                <span className="meta-value">{formData.lastLogin}</span>
                            </div>
                        </div>
                    </div>

                    {/* Detail Form */}
                    <form onSubmit={handleSave}>
                        {/* 기본 정보 섹션 */}
                        <div className="detail-section">
                            <h3 className="section-title">기본 정보</h3>
                            <div className="detail-grid">
                                <div className="detail-group">
                                    <label className="detail-label">이름</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="name"
                                            className="detail-input"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="detail-value">{formData.name}</div>
                                    )}
                                </div>

                                <div className="detail-group">
                                    <label className="detail-label">이메일</label>
                                    {isEditing ? (
                                        <input
                                            type="email"
                                            name="email"
                                            className="detail-input"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="detail-value">{formData.email}</div>
                                    )}
                                </div>

                                {isEditing && (
                                    <>
                                        <div className="detail-group">
                                            <label className="detail-label">새 비밀번호</label>
                                            <input
                                                type="password"
                                                name="password"
                                                className="detail-input"
                                                placeholder="변경할 경우에만 입력"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                minLength={8}
                                                autoComplete="new-password"
                                            />
                                        </div>

                                        <div className="detail-group">
                                            <label className="detail-label">비밀번호 확인</label>
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                className="detail-input"
                                                placeholder="비밀번호 재입력"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 조직 정보 섹션 */}
                        <div className="detail-section">
                            <h3 className="section-title">조직 정보</h3>
                            <div className="detail-grid">
                                <div className="detail-group">
                                    <label className="detail-label">전화번호</label>
                                    {isEditing ? (
                                        <input
                                            type="tel"
                                            name="phone"
                                            className="detail-input"
                                            placeholder="010-1234-5678"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                    ) : (
                                        <div className="detail-value">{formData.phone || '-'}</div>
                                    )}
                                </div>

                                <div className="detail-group">
                                    <label className="detail-label">부서</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="department"
                                            className="detail-input"
                                            placeholder="예: 경영지원팀"
                                            value={formData.department}
                                            onChange={handleInputChange}
                                        />
                                    ) : (
                                        <div className="detail-value">{formData.department || '-'}</div>
                                    )}
                                </div>

                                <div className="detail-group">
                                    <label className="detail-label">직위</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="position"
                                            className="detail-input"
                                            placeholder="예: 관리자"
                                            value={formData.position}
                                            onChange={handleInputChange}
                                        />
                                    ) : (
                                        <div className="detail-value">{formData.position || '-'}</div>
                                    )}
                                </div>

                                <div className="detail-group">
                                    <label className="detail-label">계정 상태</label>
                                    {isEditing ? (
                                        <div className="dropdown-container">
                                            <select
                                                name="status"
                                                className="detail-select"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="ACTIVE">활성</option>
                                                <option value="INACTIVE">비활성</option>
                                                <option value="LOCKED">잠김</option>
                                            </select>
                                            <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="detail-value">{mapStatus(formData.status)}</div>
                                    )}
                                </div>

                                <div className="detail-group">
                                    <label className="detail-label">권한 그룹</label>
                                    {isEditing ? (
                                        <div className="dropdown-container">
                                            <select
                                                name="roleGroupId"
                                                className="detail-select"
                                                value={formData.roleGroupId}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {roleGroups.map(rg => (
                                                    <option key={rg.id} value={rg.id}>
                                                        {rg.name} ({rg.code})
                                                    </option>
                                                ))}
                                            </select>
                                            <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="detail-value">
                                            {selectedRoleGroup?.name || '-'}
                                            {selectedRoleGroup?.code && (
                                                <span className="role-code-badge"> ({selectedRoleGroup.code})</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 권한 설정 섹션 (읽기 전용) */}
                        <div className="detail-section">
                            <h3 className="section-title">
                                권한 설정
                                <span className="subtitle-note"> (권한 그룹에 따라 자동 설정됨)</span>
                            </h3>
                            <div className="permissions-info">
                                {selectedRoleGroup?.permissions && selectedRoleGroup.permissions.length > 0 ? (
                                    <div className="permissions-tree">
                                        {Object.entries(
                                            selectedRoleGroup.permissions.reduce((acc, perm) => {
                                                const category = perm.category || '기타';
                                                if (!acc[category]) {
                                                    acc[category] = [];
                                                }
                                                acc[category].push(perm);
                                                return acc;
                                            }, {})
                                        ).map(([category, perms]) => (
                                            <div key={category} className="permission-category">
                                                <div className="category-header">
                                                    <span className="category-icon">📁</span>
                                                    <span className="category-name">{category}</span>
                                                    <span className="permission-count">({perms.length})</span>
                                                </div>
                                                <div className="category-permissions">
                                                    {perms.map((perm) => (
                                                        <div key={perm.id} className="permission-item granted">
                                                            <span className="permission-icon">✓</span>
                                                            <span className="permission-label">{perm.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="detail-value">권한이 설정되지 않았습니다.</div>
                                )}
                            </div>
                        </div>

                        {/* Form Actions */}
                        {isEditing && (
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={handleCancel}>
                                    취소
                                </button>
                                <button type="submit" className="btn-primary">
                                    저장
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </MainLayout>
    );
}

export default UserDetailPage;
